import {
  TEXTURE_GRID_SIZE,
  TEXTURE_GRID_SIZE_OPTIONS,
  isValidTextureGridSize,
  textureGridSizeFromPixels,
  type TextureGridSize,
  type TexturePixels,
} from '@shared/protocol';

const FACE_COUNT = 6;

/**
 * Minecraft box-UV unwrap (same as player-head / entity cube templates).
 * For face size S the atlas is 4S × 2S (32×16 when S=8, 64×32 when S=16):
 *
 * ```
 *      [ Top  ][Bottom]
 * [Right][Front][Left ][Back ]
 * ```
 *
 * Face indices match Three.js / BlankForge: +X, -X, +Y, -Y, +Z, -Z
 * (east, west, up, down, south, north).
 */
function boxUvOffset(face: number, size: number): { x: number; y: number } {
  switch (face) {
    case 0: return { x: 0, y: size }; // +X east / right
    case 1: return { x: size * 2, y: size }; // -X west / left
    case 2: return { x: size, y: 0 }; // +Y up / top
    case 3: return { x: size * 2, y: 0 }; // -Y down / bottom
    case 4: return { x: size, y: size }; // +Z south / front
    case 5: return { x: size * 3, y: size }; // -Z north / back
    default: return { x: 0, y: 0 };
  }
}

/** Legacy BlankForge 3×2 packed atlas (24×16 when S=8). */
function legacyOffset(face: number, size: number): { x: number; y: number } {
  return { x: (face % 3) * size, y: Math.floor(face / 3) * size };
}

/** Build a downloadable Minecraft box-UV atlas PNG. */
export function downloadTextureAtlas(name: string, pixels: TexturePixels): void {
  const canvas = atlasCanvas(pixels);
  const link = document.createElement('a');
  link.href = canvas.toDataURL('image/png');
  link.download = `${sanitizeFilename(name) || 'texture'}.png`;
  link.click();
}

/**
 * Parse an uploaded image into six face maps.
 * Accepts Minecraft box-UV (2:1, e.g. 32×16 or 64×32), legacy 3×2 (3:2),
 * or a single square face (copied to all six sides).
 * Face size is detected from the image (4 / 8 / 16); unsupported sizes error.
 * Atlas images are top-down; storage is bottom-up to match craft-ray `v = y`.
 */
export async function texturePixelsFromImageFile(file: File): Promise<TexturePixels> {
  const bitmap = await createImageBitmap(file);
  try {
    const aspect = bitmap.width / Math.max(1, bitmap.height);
    const layout = aspect > 1.75 ? 'box' : aspect > 1.25 ? 'legacy' : 'single';
    const size = resolveUploadedFaceSize(bitmap.width, bitmap.height, layout);
    if (!size) {
      throw new Error(
        `Texture face size must be ${TEXTURE_GRID_SIZE_OPTIONS.join(', ')} ` +
          `(box-UV ${TEXTURE_GRID_SIZE_OPTIONS.map((s) => `${s * 4}×${s * 2}`).join(' / ')}, ` +
          `or a square face).`,
      );
    }
    const canvas = document.createElement('canvas');
    if (layout === 'box') {
      canvas.width = size * 4;
      canvas.height = size * 2;
    } else if (layout === 'legacy') {
      canvas.width = size * 3;
      canvas.height = size * 2;
    } else {
      canvas.width = size;
      canvas.height = size;
    }
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    if (layout === 'single') {
      const face = readFace(ctx, 0, 0, size);
      return Array.from({ length: FACE_COUNT }, () => [...face]);
    }

    const offsetOf = layout === 'box' ? boxUvOffset : legacyOffset;
    const pixels: TexturePixels = [];
    for (let face = 0; face < FACE_COUNT; face++) {
      const { x, y } = offsetOf(face, size);
      pixels.push(readFace(ctx, x, y, size));
    }
    return pixels;
  } finally {
    bitmap.close();
  }
}

/**
 * Pick face size from atlas dimensions.
 * Exact option match wins; otherwise snap to nearest supported size and resample.
 */
function resolveUploadedFaceSize(
  width: number,
  height: number,
  layout: 'box' | 'legacy' | 'single',
): TextureGridSize | null {
  let raw: number;
  if (layout === 'box') {
    raw = height / 2;
    if (Math.abs(width / 4 - raw) > 0.51) return null;
  } else if (layout === 'legacy') {
    raw = height / 2;
    if (Math.abs(width / 3 - raw) > 0.51) return null;
  } else {
    if (Math.abs(width - height) > 0.51) return null;
    raw = width;
  }
  if (isValidTextureGridSize(raw)) return raw;
  // Nearest supported size so e.g. 512² still imports as 16².
  let best: TextureGridSize = TEXTURE_GRID_SIZE;
  let bestDist = Infinity;
  for (const option of TEXTURE_GRID_SIZE_OPTIONS) {
    const dist = Math.abs(option - raw);
    if (dist < bestDist) {
      best = option;
      bestDist = dist;
    }
  }
  return best;
}

function atlasCanvas(pixels: TexturePixels): HTMLCanvasElement {
  const size = textureGridSizeFromPixels(pixels) ?? TEXTURE_GRID_SIZE;
  const canvas = document.createElement('canvas');
  canvas.width = size * 4;
  canvas.height = size * 2;
  const ctx = canvas.getContext('2d')!;
  // Transparent padding in the empty top-row corners (Minecraft box-UV).
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  for (let face = 0; face < FACE_COUNT; face++) {
    const { x: offsetX, y: offsetY } = boxUvOffset(face, size);
    pixels[face]?.forEach((color, index) => {
      ctx.fillStyle = color.length === 9 ? color : `${color}ff`;
      const row = size - 1 - Math.floor(index / size);
      ctx.fillRect(offsetX + (index % size), offsetY + row, 1, 1);
    });
  }
  return canvas;
}

function readFace(
  ctx: CanvasRenderingContext2D,
  offsetX: number,
  offsetY: number,
  size: number,
): string[] {
  const facePixels = Array.from({ length: size * size }, () => '#00000000');
  const data = ctx.getImageData(offsetX, offsetY, size, size).data;
  for (let atlasIndex = 0; atlasIndex < size * size; atlasIndex++) {
    const offset = atlasIndex * 4;
    const column = atlasIndex % size;
    const atlasRow = Math.floor(atlasIndex / size);
    const storageIndex = (size - 1 - atlasRow) * size + column;
    facePixels[storageIndex] = rgbaHex(
      data[offset],
      data[offset + 1],
      data[offset + 2],
      data[offset + 3],
    );
  }
  return facePixels;
}

function rgbaHex(r: number, g: number, b: number, a: number): string {
  return `#${[r, g, b, a].map((value) => value.toString(16).padStart(2, '0')).join('')}`;
}

function sanitizeFilename(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9-_]+/g, '-').replace(/^-|-$/g, '');
}
